import { Controller } from "@hotwired/stimulus"

// QAN-739: polling live-updates for the quiz results page. The page renders
// once and used to go stale — an admin claiming the deal, unlocking the quote
// or a consultation booked from another tab showed nothing until a manual
// refresh. No ActionCable (configured but dead — zero channels) and no Turbo
// on this page (the assessment layouts load the `application` pack:
// Turbolinks + Stimulus + jQuery), so this follows the codebase's own
// fingerprint-poll + fragment-re-render + DOM-swap pattern (see
// admin/business_loan_applications/tabs/_conversation.html.erb).
//
// Each tick fetches the status JSON (urlValue). If the server fingerprint
// matches ours, nothing happens. If quote_locked or the Credit Data step
// flipped relative to what this page was rendered with, the whole page
// reloads — those flips change Alpine-heavy regions and the credit-modal /
// opener set, which a partial swap can't honestly represent. Any other
// movement fetches the fragment (fragmentUrlValue) and swaps the innerHTML of
// #journey-stepper and #quote-state, then re-runs the QAN-677 auto-pop so a
// freshly claimed deal pops its booking popup without a reload.
//
// Markup pattern (assessments/results.html.erb):
//   <div data-controller="live-state"
//        data-live-state-url-value="..."           (status JSON endpoint)
//        data-live-state-fragment-url-value="..."  (live fragment endpoint)
//        data-live-state-fingerprint-value="..."   (server-rendered initial)
//        data-live-state-quote-locked-value="true"
//        data-live-state-step2-done-value="false">
export default class extends Controller {
  static values = {
    url: String,
    fragmentUrl: String,
    fingerprint: String,
    quoteLocked: Boolean,
    step2Done: Boolean,
    cardKey: String,
    interval: { type: Number, default: 15000 }
  }

  connect() {
    this._teardown = this._teardown || this.teardown.bind(this)
    this.timer = setInterval(this.tick.bind(this), this.intervalValue)
    // This page runs under Turbolinks, and nothing disconnects a controller on
    // a bfcache restore — take the timer down with the page either way.
    window.addEventListener("pagehide", this._teardown)
    document.addEventListener("turbolinks:before-cache", this._teardown)
  }

  disconnect() {
    this.teardown()
  }

  teardown() {
    if (this.timer) clearInterval(this.timer)
    this.timer = null
    window.removeEventListener("pagehide", this._teardown)
    document.removeEventListener("turbolinks:before-cache", this._teardown)
  }

  tick() {
    // Nothing to refresh while the tab is in the background, and never yank
    // the DOM out from under an open booking surface — the QAN-745 custom
    // booking modal (a full reload would kill a booking in progress) or
    // Calendly's own overlay on the fallback path.
    if (document.hidden) return
    if (document.querySelector(".calendly-overlay")) return
    if (document.querySelector("#qanBookingModal:not(.hidden)")) return
    if (this.inFlight) return
    this.inFlight = true

    fetch(this.urlValue, {
      headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" },
      credentials: "same-origin"
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data || !data.fingerprint) return
        if (data.fingerprint === this.fingerprintValue) return

        // Compared against the values this page was RENDERED with (delivered
        // as data-values), not the last poll — the reload path must win even
        // if a swap tick already advanced the fingerprint.
        // QAN-747/748: the unlocked quote card is Alpine-driven and
        // refreshRegions never swaps it — a specialist editing the quote,
        // unlocking pricing, or an acceptance from another surface takes the
        // full reload path. The rule's reason stands: no hand-mutation of
        // card internals, no duplicated render logic. QAN-783 adds the one
        // sanctioned alternative: the plan-toggle endpoint returns the WHOLE
        // server-rendered card partial and the page swaps #quote-card + lets
        // Alpine re-init the subtree (see swapQuoteCard in results.html.erb),
        // updating this controller's card-key value so the swap doesn't
        // trigger a redundant reload here. Cross-tab, this reload path is
        // still how other open tabs catch up. The card's URL-hash tab sync
        // restores the client's tab across either refresh.
        if (Boolean(data.quote_locked) !== this.quoteLockedValue ||
            Boolean(data.step2_done) !== this.step2DoneValue ||
            (this.hasCardKeyValue && data.card_key && data.card_key !== this.cardKeyValue)) {
          window.location.reload()
          return
        }

        return this.refreshRegions(data.fingerprint)
      })
      .catch(() => { /* transient network error — the next tick retries */ })
      .finally(() => { this.inFlight = false })
  }

  refreshRegions(fingerprint) {
    return fetch(this.fragmentUrlValue, {
      headers: { "X-Requested-With": "XMLHttpRequest" },
      credentials: "same-origin"
    })
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => {
        if (!html) return
        const doc = new DOMParser().parseFromString(html, "text/html")
        ;["journey-stepper", "quote-state"].forEach((id) => {
          const fresh = doc.getElementById(id)
          const current = document.getElementById(id)
          if (fresh && current) current.innerHTML = fresh.innerHTML
        })
        this.fingerprintValue = fingerprint
        // QAN-677 auto-pop: if the swap just delivered a bookable CTA
        // (data-auto-pop-url), open the booking popup. Session-guarded
        // single-fire inside the function itself.
        if (typeof window.__qanAutoPopBooking === "function") window.__qanAutoPopBooking()
      })
  }
}
